import { useState } from 'react';
import { ensureDateOnly, ensureVersion } from './releaseNotesUtils';
import historicalReleaseNotesJson from './historicalReleaseNotes.json';

interface ReleaseNote {
  version: string;
  date: string;
  features: string[];
  fixes?: string[];
  improvements?: string[];
}

// Get current version info from build
function getCurrentVersionInfo() {
  const version = ensureVersion(process.env.REACT_APP_VERSION);
  const gitSha = process.env.REACT_APP_GIT_SHA || '';
  const gitTag = process.env.REACT_APP_GIT_TAG || '';

  return {
    version,
    gitSha: gitSha.slice(0, 7),
    gitTag: gitTag.slice(0, 7),
    date: ensureDateOnly(new Date().toISOString()),
  };
}

const HISTORICAL_RELEASE_NOTES: ReleaseNote[] = (Array.isArray(historicalReleaseNotesJson)
  ? historicalReleaseNotesJson
  : []) as ReleaseNote[];

export function ReleaseNotes() {
  const [expanded, setExpanded] = useState(false);
  const currentVersion = getCurrentVersionInfo();

  // Build current release note
  const currentRelease: ReleaseNote = {
    version: ensureVersion(currentVersion.version),
    date: ensureDateOnly(currentVersion.date),
    features: [
      'Virtual Interview Practice — AI mock interviews from Resume Tuner',
      'Interview debrief with score, strengths, improvements & resources',
      'Duration selector with free/premium tiers (10m, 15m, 20m, 30m)',
      'AI Critique — get LLM-powered resume improvement suggestions',
    ],
    improvements: [
      'Dashboard release notes show version only (dates removed)',
      'Resume Tuner results now include Practice Interview CTA',
      'Full i18n support for Interview Practice (English & French)',
    ],
  };

  // Combine current release with historical releases from changelog sync output
  const historicalExcludingCurrent = HISTORICAL_RELEASE_NOTES.filter(
    (release) => ensureVersion(release.version) !== ensureVersion(currentRelease.version)
  );

  const allReleases = [currentRelease, ...historicalExcludingCurrent].map((release) => ({
    ...release,
    version: ensureVersion(release.version),
    date: ensureDateOnly(release.date),
  }));
  const latestRelease = allReleases[0];
  const olderReleases = allReleases.slice(1);

  return (
    <div style={{
      marginTop: '24px',
      padding: '16px 20px',
      backgroundColor: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: '8px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: expanded ? '12px' : '0',
      }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
          📋 Release Notes
        </h3>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            padding: '4px 10px',
            fontSize: '12px',
            backgroundColor: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            cursor: 'pointer',
            color: 'var(--text-muted)',
          }}
        >
          {expanded ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {/* Latest version always visible */}
      <div style={{ marginTop: '12px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
        }}>
          <span style={{
            backgroundColor: 'var(--primary)',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
          }}>
            v{latestRelease.version}
          </span>
          <span style={{
            backgroundColor: '#22c55e',
            color: 'white',
            padding: '1px 6px',
            borderRadius: '3px',
            fontSize: '10px',
            fontWeight: 600,
          }}>
            LATEST
          </span>
        </div>

        {/* Feature summary (always visible) */}
        <p style={{
          fontSize: '13px',
          color: 'var(--text)',
          margin: '0 0 4px 0',
        }}>
          {latestRelease.features[0]}
          {latestRelease.features.length > 1 && !expanded && (
            <span style={{ color: 'var(--text-muted)' }}>
              {' '}(+{latestRelease.features.length - 1} more)
            </span>
          )}
        </p>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ marginTop: '16px' }}>
          {/* Full features list for latest */}
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 8px 0' }}>
              ✨ New Features
            </h4>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '13px',
              lineHeight: 1.6,
            }}>
              {latestRelease.features.map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
          </div>

          {latestRelease.improvements && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 8px 0' }}>
                🔧 Improvements
              </h4>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '13px',
                lineHeight: 1.6,
              }}>
                {latestRelease.improvements.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {latestRelease.fixes && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 8px 0' }}>
                🐛 Bug Fixes
              </h4>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '13px',
                lineHeight: 1.6,
              }}>
                {latestRelease.fixes.map((fix, i) => (
                  <li key={i}>{fix}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Older releases */}
          {olderReleases.length > 0 && (
            <div style={{
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid var(--border)',
            }}>
              <h4 style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 12px 0' }}>
                Previous Releases
              </h4>
              {olderReleases.map((release) => (
                <div key={release.version} style={{ marginBottom: '12px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px',
                  }}>
                    <span style={{
                      backgroundColor: 'var(--surface-muted)',
                      color: 'var(--text)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}>
                      v{release.version}
                    </span>
                  </div>
                  <ul style={{
                    margin: '4px 0 0 0',
                    paddingLeft: '20px',
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    lineHeight: 1.5,
                  }}>
                    {release.features.slice(0, 3).map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                    {release.features.length > 3 && (
                      <li style={{ listStyle: 'none', marginLeft: '-12px' }}>
                        ...and {release.features.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
